import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Wallet, PackageMinus, TrendingUp, Receipt, Download, Search } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { KpiCard } from '@/components/ui/KpiCard';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { formatINR } from '@/lib/format';
import { getSalesRegister, type SalesLine } from '@/api/accounts';
import { SegGroup } from './SegGroup';
import { RegisterTable } from './RegisterTable';

const money = (v: number) => <span className="tabular-nums">{formatINR(v)}</span>;
const Profit = ({ v, loss }: { v: number; loss?: boolean }) => (
  <span className={`tabular-nums font-bold ${loss ? 'text-danger' : 'text-success'}`}>{formatINR(v)}</span>
);
const Pct = ({ v, loss }: { v: number; loss?: boolean }) => (
  <span className={`tabular-nums font-bold ${loss ? 'text-danger' : 'text-success'}`}>{v.toFixed(1)}%</span>
);

type View = 'line' | 'cust' | 'item' | 'hsn';

export function SalesRegisterPage() {
  const [view, setView] = useState<View>('line');
  // All / Date (from–to) / Month / Quarter / Year — opens on the current month.
  const period = usePeriod('month');
  const { range } = period;
  const [customer, setCustomer] = useState('');
  const [vendor, setVendor] = useState('');
  const [search, setSearch] = useState('');
  const [exportTick, setExportTick] = useState(0);
  const viewName = { line: 'By_Invoice_Line', cust: 'By_Customer', item: 'By_Item', hsn: 'By_HSN' }[view];
  const exp = { exportSignal: exportTick, exportName: `Sales_Register_${viewName}` };

  const query = useQuery({
    queryKey: ['accounts', 'sales-register', range?.from ?? '', range?.to ?? ''],
    queryFn: () => getSalesRegister({ from: range?.from, to: range?.to }),
  });
  const allLines = useMemo(() => query.data?.lines ?? [], [query.data]);

  const custOpts = useMemo(() => [...new Set(allLines.map((l) => l.customer))].sort(), [allLines]);
  const vendorOpts = useMemo(() => [...new Set(allLines.map((l) => l.vendor))].sort(), [allLines]);

  const lines = useMemo(
    () => allLines.filter((l) => (!customer || l.customer === customer) && (!vendor || l.vendor === vendor)),
    [allLines, customer, vendor],
  );

  const kpis = useMemo(() => {
    const grossSales = lines.reduce((s, l) => s + l.taxable, 0);
    const cogs = lines.reduce((s, l) => s + l.cost, 0);
    const gst = lines.reduce((s, l) => s + l.tax, 0);
    return { grossSales, cogs, grossProfit: grossSales - cogs, margin: grossSales > 0 ? ((grossSales - cogs) / grossSales) * 100 : 0, gst };
  }, [lines]);

  const byCustomer = useMemo(() => groupSales(lines, (l) => l.customer).map(([customer, g]) => ({ customer, ...g })), [lines]);
  const byItem = useMemo(() => groupSales(lines, (l) => l.item).map(([item, g]) => ({ item, ...g })), [lines]);
  const byHsn = useMemo(() => groupHsn(lines), [lines]);

  return (
    <div>
      <SectionHeader
        title="Sales Register"      />

      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Gross Sales (taxable)" value={`₹${formatINR(kpis.grossSales)}`} accent="#FF8C00" Icon={Wallet} loading={query.isLoading} />
        <KpiCard label="Total Cost (COGS)" value={`₹${formatINR(kpis.cogs)}`} accent="#5b8def" Icon={PackageMinus} loading={query.isLoading} />
        <KpiCard label={`Gross Profit · ${kpis.margin.toFixed(1)}%`} value={`₹${formatINR(kpis.grossProfit)}`} accent="#35c07f" Icon={TrendingUp} loading={query.isLoading} />
        <KpiCard label="GST Collected" value={`₹${formatINR(kpis.gst)}`} accent="#8b5cf6" Icon={Receipt} loading={query.isLoading} />
      </div>

      <SegGroup
        value={view}
        onChange={(v) => setView(v as View)}
        options={[
          { value: 'line', label: 'By Invoice line' },
          { value: 'cust', label: 'By Customer' },
          { value: 'item', label: 'By Item' },
          { value: 'hsn', label: 'By HSN' },
        ]}
      />

      <FilterBar>
        <FilterField label="Search" className="col-span-2 sm:col-auto sm:min-w-[220px]">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              title="Search by invoice, customer, item or HSN"
              className="!pl-5 sm:!pl-8"
              aria-label="Search sales register"
            />
          </div>
        </FilterField>
        <FilterField label="Customer">
          <Select value={customer} onChange={(e) => setCustomer(e.target.value)}>
            <option value="">All</option>
            {custOpts.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Source vendor">
          <Select value={vendor} onChange={(e) => setVendor(e.target.value)}>
            <option value="">All</option>
            {vendorOpts.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        <button
          type="button"
          title="Export to Excel — every row matching these filters and search"
          onClick={() => setExportTick((t) => t + 1)}
          className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] px-2 text-[11px] font-bold text-[var(--text-secondary)] shadow-sm transition hover:border-accent hover:text-accent sm:col-span-1 sm:ml-auto sm:h-8 sm:gap-1.5 sm:self-end sm:px-3 sm:text-xs"
        >
          <Download size={12} /> Export
        </button>
      </FilterBar>

      {view === 'line' && (
        <RegisterTable
          title="Invoice-line detail"
          {...exp}
          count={`${lines.length} lines`}
          search={search}
          searchText={(r) => `${r.invoice} ${r.customer} ${r.item} ${r.hsn} ${r.vendor}`}
          loading={query.isLoading}
          rows={lines}
          rowKey={(r) => `${r.invoice}|${r.item}|${r.taxable}|${r.tax}`}
          columns={[
            { key: 'invoice', header: 'Invoice', cell: (r) => <span className="font-extrabold text-accent">{r.invoice}</span> },
            { key: 'date', header: 'Date', cell: (r) => r.date },
            { key: 'customer', header: 'Customer', cell: (r) => <span className="font-bold">{r.customer}</span> },
            { key: 'item', header: 'Item', cell: (r) => r.item },
            { key: 'hsn', header: 'HSN', cell: (r) => r.hsn },
            { key: 'qty', header: 'Qty', align: 'right', cell: (r) => money(r.qty) },
            { key: 'rate', header: 'Rate', align: 'right', cell: (r) => money(r.rate) },
            { key: 'taxable', header: 'Taxable', align: 'right', cell: (r) => money(r.taxable) },
            { key: 'gst', header: 'GST%', align: 'center', cell: (r) => <span className="tabular-nums">{r.gst}</span> },
            { key: 'tax', header: 'Tax', align: 'right', cell: (r) => money(r.tax) },
            { key: 'total', header: 'Line Total', align: 'right', cell: (r) => money(r.total) },
            { key: 'cost', header: 'Cost', align: 'right', cell: (r) => money(r.cost) },
            { key: 'profit', header: 'Profit', align: 'right', cell: (r) => <Profit v={r.profit} loss={r.loss} /> },
            { key: 'margin', header: 'Margin', align: 'right', cell: (r) => <Pct v={r.margin} loss={r.loss} /> },
            { key: 'vendor', header: 'Source Vendor', cell: (r) => r.vendor },
          ]}
        />
      )}

      {view === 'cust' && (
        <RegisterTable
          {...exp} title="By customer" count={`${byCustomer.length} customers`} search={search} searchText={(r) => r.customer} loading={query.isLoading}
          rows={byCustomer} rowKey={(r) => r.customer}
          columns={[
            { key: 'customer', header: 'Customer', cell: (r) => <span className="font-bold">{r.customer}</span> },
            { key: 'invoices', header: 'Invoices', align: 'right', cell: (r) => <span className="tabular-nums">{r.invoices}</span> },
            { key: 'qty', header: 'Qty', align: 'right', cell: (r) => money(r.qty) },
            { key: 'taxable', header: 'Taxable', align: 'right', cell: (r) => money(r.taxable) },
            { key: 'gst', header: 'GST', align: 'right', cell: (r) => money(r.tax), value: (r) => r.tax },
            { key: 'total', header: 'Total', align: 'right', cell: (r) => money(r.total) },
            { key: 'cost', header: 'Cost', align: 'right', cell: (r) => money(r.cost) },
            { key: 'profit', header: 'Profit', align: 'right', cell: (r) => <Profit v={r.profit} /> },
            { key: 'margin', header: 'Margin', align: 'right', cell: (r) => <Pct v={r.margin} /> },
          ]}
        />
      )}

      {view === 'item' && (
        <RegisterTable
          {...exp} title="By item" count={`${byItem.length} items`} search={search} searchText={(r) => `${r.item} ${r.hsn} ${r.vendor}`} loading={query.isLoading}
          rows={byItem} rowKey={(r) => r.item}
          columns={[
            { key: 'item', header: 'Item', cell: (r) => <span className="font-bold">{r.item}</span> },
            { key: 'hsn', header: 'HSN', cell: (r) => r.hsn },
            { key: 'qty', header: 'Qty sold', align: 'right', cell: (r) => money(r.qty) },
            { key: 'taxable', header: 'Taxable', align: 'right', cell: (r) => money(r.taxable) },
            { key: 'cost', header: 'Cost', align: 'right', cell: (r) => money(r.cost) },
            { key: 'profit', header: 'Profit', align: 'right', cell: (r) => <Profit v={r.profit} /> },
            { key: 'margin', header: 'Margin', align: 'right', cell: (r) => <Pct v={r.margin} /> },
            { key: 'vendor', header: 'Source vendor', cell: (r) => r.vendor },
          ]}
        />
      )}

      {view === 'hsn' && (
        <RegisterTable
          {...exp} title="By HSN (GSTR-1 summary)" count={`${byHsn.length} HSN`} search={search} searchText={(r) => r.hsn} loading={query.isLoading}
          rows={byHsn} rowKey={(r) => r.hsn}
          columns={[
            { key: 'hsn', header: 'HSN', cell: (r) => <span className="font-extrabold text-accent">{r.hsn}</span> },
            { key: 'qty', header: 'Qty', align: 'right', cell: (r) => money(r.qty) },
            { key: 'taxable', header: 'Taxable', align: 'right', cell: (r) => money(r.taxable) },
            { key: 'gst', header: 'GST%', align: 'center', cell: (r) => <span className="tabular-nums">{r.gst}</span> },
            { key: 'tax', header: 'GST amount', align: 'right', cell: (r) => money(r.tax) },
            { key: 'total', header: 'Total', align: 'right', cell: (r) => money(r.total) },
          ]}
        />
      )}
    </div>
  );
}

function groupSales(lines: SalesLine[], keyOf: (l: SalesLine) => string) {
  const m = new Map<string, { invoices: number; qty: number; taxable: number; tax: number; total: number; cost: number; profit: number; margin: number; hsn: string; vendor: string; _inv: Set<string> }>();
  for (const l of lines) {
    const k = keyOf(l);
    let g = m.get(k);
    if (!g) { g = { invoices: 0, qty: 0, taxable: 0, tax: 0, total: 0, cost: 0, profit: 0, margin: 0, hsn: l.hsn, vendor: l.vendor, _inv: new Set() }; m.set(k, g); }
    g._inv.add(l.invoice); g.qty += l.qty; g.taxable += l.taxable; g.tax += l.tax; g.total += l.total; g.cost += l.cost; g.profit += l.profit; g.vendor = l.vendor;
  }
  return Array.from(m.entries())
    .map(([k, g]) => [k, { ...g, invoices: g._inv.size, margin: g.taxable > 0 ? (g.profit / g.taxable) * 100 : 0 }] as const)
    .sort((a, b) => b[1].taxable - a[1].taxable);
}

function groupHsn(lines: SalesLine[]) {
  const m = new Map<string, { qty: number; taxable: number; tax: number; total: number; rates: Set<number> }>();
  for (const l of lines) {
    let g = m.get(l.hsn);
    if (!g) { g = { qty: 0, taxable: 0, tax: 0, total: 0, rates: new Set() }; m.set(l.hsn, g); }
    g.qty += l.qty; g.taxable += l.taxable; g.tax += l.tax; g.total += l.total; g.rates.add(l.gst);
  }
  return Array.from(m.entries())
    .map(([hsn, g]) => ({ hsn, qty: g.qty, taxable: g.taxable, gst: Array.from(g.rates).sort((a, b) => a - b).join(' / '), tax: g.tax, total: g.total }))
    .sort((a, b) => b.taxable - a.taxable);
}
